import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { NotebookPen, FileSpreadsheet, ScrollText, Camera, PenLine, Table2, UploadCloud, History, AlertTriangle, CheckCircle2, Trash2 } from 'lucide-react';
import { API_BASE } from '../config';
import { hasEditAccess } from '../permissionConfig';

// PO Register Data — replaces the hand-typed Register_PO_Record.xlsx. A
// photo of the handwritten dispatch register is uploaded, read twice by two
// AI models and cross-checked server-side (po-register.js), and auto-saved;
// cells the reads disagree on / found unclear are yellow "Recheck" cells,
// fixed inline here. Layout and tokens follow GiftingDispatch.jsx.

// ── Date helpers — local duplicates, same as every other page ──────────
function pad(n) { return String(n).padStart(2, '0'); }
function toISO(y, m, d) { return `${y}-${pad(m)}-${pad(d)}`; }
function isoOffset(days) { const d = new Date(); d.setDate(d.getDate() + days); return toISO(d.getFullYear(), d.getMonth() + 1, d.getDate()); }
const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
// Same "30-Oct-26" shape as the register Excel's Date column / sheet names.
function registerDate(value) {
    if (!value) return '—';
    const s = String(value).slice(0, 10);
    const [y, m, d] = s.split('-').map(Number);
    if (!y || !m || !d) return '—';
    return `${d}-${MON[m - 1]}-${String(y).slice(2)}`;
}
const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const MONTH_YEAR_OPTIONS = (() => {
    const year = new Date().getFullYear();
    const opts = [];
    for (const y of [year - 1, year]) for (let m = 1; m <= 12; m++) opts.push({ value: `${y}-${pad(m)}`, label: `${MONTH_NAMES[m - 1]} ${y}` });
    return opts;
})();

const inr = n => (n === null || n === undefined || n === '' ? '—' : Number(n).toLocaleString('en-IN', { maximumFractionDigits: 2 }));
const two = n => (n === null || n === undefined || n === '' ? '—' : String(Number(n)).padStart(2, '0')); // Box/Weight show "04" like the Excel

const COLUMNS = [
    { key: 'dispatch_date',  label: 'Date',          width: '0.8fr', render: r => registerDate(r.dispatch_date), editAs: 'date' },
    { key: 'party_name',     label: 'Party Name',    width: '1.6fr', bold: true },
    { key: 'location',       label: 'Location',      width: '1fr' },
    { key: 'po_number',      label: 'PO Number',     width: '1.3fr', mono: true },
    { key: 'invoice_number', label: 'Invoice No',    width: '1.15fr', mono: true, render: r => r.invoice_number || '-' },
    { key: 'po_qty',         label: 'PO Qty',        width: '0.7fr', num: true, render: r => inr(r.po_qty) },
    { key: 'po_amount',      label: 'PO Amount',     width: '1fr', num: true, render: r => inr(r.po_amount) },
    { key: 'box',            label: 'Box',           width: '0.5fr', num: true, render: r => two(r.box) },
    { key: 'weight',         label: 'Weight',        width: '0.6fr', num: true, render: r => two(r.weight) },
    { key: 'dispatched_by',  label: 'Dispatched By', width: '0.95fr' },
];
const GRID_COLS = `0.35fr ${COLUMNS.map(c => c.width).join(' ')} 0.45fr`;

const ACCEPT = 'image/*,.pdf,.zip,.xlsx,.xls,.csv';
const IMAGE_RE = /\.(jpe?g|png|webp|gif|bmp)$/i;
// Phone photos are 4–12 MB; the models don't need more than ~3000px on the
// long side to read the register, and smaller uploads keep each request fast.
const MAX_SIDE = 3000;

async function compressImage(file) {
    if (file.size < 2.5 * 1024 * 1024) return file;
    try {
        const bitmap = await createImageBitmap(file);
        const scale = Math.min(1, MAX_SIDE / Math.max(bitmap.width, bitmap.height));
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(bitmap.width * scale);
        canvas.height = Math.round(bitmap.height * scale);
        canvas.getContext('2d').drawImage(bitmap, 0, 0, canvas.width, canvas.height);
        const blob = await new Promise(res => canvas.toBlob(res, 'image/jpeg', 0.92));
        return blob ? new File([blob], file.name.replace(/\.[^.]+$/, '') + '.jpg', { type: 'image/jpeg' }) : file;
    } catch {
        return file; // e.g. HEIC on a browser that can't decode it — server explains
    }
}

// Zips are opened here (not on the server) so each photo inside goes up as
// its own request — keeps every request well inside the server time limit.
async function expandFiles(fileList) {
    const out = [];
    for (const f of fileList) {
        if (/\.zip$/i.test(f.name)) {
            const JSZip = (await import('jszip')).default;
            const zip = await JSZip.loadAsync(f);
            const entries = Object.values(zip.files)
                .filter(e => !e.dir && !e.name.startsWith('__MACOSX') && !/(^|\/)\./.test(e.name))
                .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));
            for (const e of entries) {
                const name = e.name.split('/').pop();
                const blob = await e.async('blob');
                const type = IMAGE_RE.test(name) ? `image/${name.split('.').pop().toLowerCase().replace('jpg', 'jpeg')}` : (/\.pdf$/i.test(name) ? 'application/pdf' : '');
                out.push(new File([blob], name, { type }));
            }
        } else {
            out.push(f);
        }
    }
    return Promise.all(out.map(f => (IMAGE_RE.test(f.name) || /^image\//.test(f.type) ? compressImage(f) : f)));
}

const STAGES = [
    { at: 0,     text: 'Uploading…' },
    { at: 3000,  text: 'Reading the handwriting — 2 independent reads…' },
    { at: 30000, text: 'Cross-checking both reads, cell by cell…' },
    { at: 60000, text: 'Checking party names, invoice formats & duplicates…' },
];

export default function PORegisterData({ user }) {
    const canEdit = hasEditAccess(user, 'po_so_lifecycle', 'po_register_data');
    const username = user?.username || '';

    // Default view = calendar D-1 + today, per explicit instruction (not
    // "last day with data", even when D-1 is a Sunday).
    const [fromDate, setFromDate] = useState(isoOffset(-1));
    const [toDate, setToDate] = useState(isoOffset(0));
    const [quickRange, setQuickRange] = useState('d1');
    const [selectedMonthYear, setSelectedMonthYear] = useState('');

    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState('');
    const [recheckOnly, setRecheckOnly] = useState(false);
    const [toast, setToast] = useState('');

    const [dragOver, setDragOver] = useState(false);
    const [uploading, setUploading] = useState(null); // { index, total, name, stage }
    const [uploadResult, setUploadResult] = useState(null);
    const fileInputRef = useRef(null);
    const stageTimers = useRef([]);

    const [editing, setEditing] = useState(null); // { id, key, value }
    const [savingCell, setSavingCell] = useState(false);
    const [historyOpen, setHistoryOpen] = useState(false);
    const [uploads, setUploads] = useState([]);
    const [downloading, setDownloading] = useState(false);

    const loadRows = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const qs = new URLSearchParams({ action: 'list', username });
            if (fromDate) qs.set('date_from', fromDate);
            if (toDate) qs.set('date_to', toDate);
            const res = await fetch(`${API_BASE}/po-register?${qs.toString()}`);
            const data = await res.json();
            if (!res.ok || data.success === false) throw new Error(data.error || 'Failed to load');
            setRows(data.rows || []);
        } catch (err) {
            setError(err.message);
        }
        setLoading(false);
    }, [fromDate, toDate, username]);

    useEffect(() => { loadRows(); }, [loadRows]);

    function showToast(msg) {
        setToast(msg);
        setTimeout(() => setToast(''), 3500);
    }

    function applyQuickRange(key) {
        setQuickRange(key);
        setSelectedMonthYear('');
        if (key === 'd1')        { setFromDate(isoOffset(-1)); setToDate(isoOffset(0)); }
        if (key === 'yesterday') { setFromDate(isoOffset(-1)); setToDate(isoOffset(-1)); }
        if (key === 'today')     { setFromDate(isoOffset(0));  setToDate(isoOffset(0)); }
        if (key === 'last7')     { setFromDate(isoOffset(-6)); setToDate(isoOffset(0)); }
    }
    function onManualDateChange(setter, value) {
        setter(value);
        setQuickRange(null);
        setSelectedMonthYear('');
    }
    function applyMonthYear(value) {
        setSelectedMonthYear(value);
        setQuickRange(null);
        if (!value) return;
        const [y, m] = value.split('-').map(Number);
        setFromDate(toISO(y, m, 1));
        setToDate(toISO(y, m, new Date(y, m, 0).getDate()));
    }

    const filteredRows = useMemo(() => {
        let list = rows;
        if (recheckOnly) list = list.filter(r => r.needs_recheck);
        const q = search.trim().toLowerCase();
        if (!q) return list;
        return list.filter(r => `${r.party_name} ${r.location || ''} ${r.po_number || ''} ${r.invoice_number || ''} ${r.dispatched_by || ''}`.toLowerCase().includes(q));
    }, [rows, search, recheckOnly]);

    // Grouped by date, newest first — each day reads like one sheet of the
    // old workbook, with its own subtotal line.
    const groups = useMemo(() => {
        const map = new Map();
        for (const r of filteredRows) {
            const d = String(r.dispatch_date).slice(0, 10);
            if (!map.has(d)) map.set(d, []);
            map.get(d).push(r);
        }
        return [...map.entries()].map(([date, list]) => ({
            date, list,
            qty: list.reduce((s, r) => s + (Number(r.po_qty) || 0), 0),
            amount: list.reduce((s, r) => s + (Number(r.po_amount) || 0), 0),
        }));
    }, [filteredRows]);

    const summary = useMemo(() => ({
        rows: rows.length,
        qty: rows.reduce((s, r) => s + (Number(r.po_qty) || 0), 0),
        amount: rows.reduce((s, r) => s + (Number(r.po_amount) || 0), 0),
        boxes: rows.reduce((s, r) => s + (Number(r.box) || 0), 0),
        weight: rows.reduce((s, r) => s + (Number(r.weight) || 0), 0),
        recheck: rows.filter(r => r.needs_recheck).length,
    }), [rows]);

    const rangeLabel = useMemo(() => {
        if (quickRange === 'd1') return `D-1 + Today (${registerDate(fromDate)} – ${registerDate(toDate)})`;
        if (selectedMonthYear) return MONTH_YEAR_OPTIONS.find(o => o.value === selectedMonthYear)?.label || selectedMonthYear;
        if (fromDate && toDate) return fromDate === toDate ? registerDate(fromDate) : `${registerDate(fromDate)} – ${registerDate(toDate)}`;
        return 'All dates';
    }, [quickRange, selectedMonthYear, fromDate, toDate]);

    // ── Upload — one request per file, sequentially ──────────────────────
    async function handleFiles(fileList) {
        if (!fileList?.length || uploading) return;
        if (!canEdit) { showToast('You have view-only access to PO Register Data'); return; }
        setUploadResult(null);
        let files;
        try {
            setUploading({ index: 0, total: fileList.length, name: '', stage: 'Preparing files…' });
            files = await expandFiles([...fileList]);
        } catch (err) {
            setUploading(null);
            setUploadResult({ error: `Could not open the files: ${err.message}` });
            return;
        }
        const agg = { rows_saved: 0, rows_flagged: 0, rows_already_saved: 0, rows_found: 0, by_date: {}, warnings: [], errors: [], other_dates: new Set() };
        for (let i = 0; i < files.length; i++) {
            const f = files[i];
            setUploading({ index: i + 1, total: files.length, name: f.name, stage: STAGES[0].text });
            stageTimers.current.forEach(clearTimeout);
            stageTimers.current = STAGES.slice(1).map(s => setTimeout(() => setUploading(u => (u ? { ...u, stage: s.text } : u)), s.at));
            try {
                const fd = new FormData();
                fd.append('username', username);
                fd.append('files', f, f.name);
                const res = await fetch(`${API_BASE}/po-register?action=upload`, { method: 'POST', body: fd });
                const data = await res.json().catch(() => ({ success: false, error: `Server error ${res.status}` }));
                if (!res.ok || !data.success) throw new Error(data.error || `Upload failed (${res.status})`);
                agg.rows_saved += data.rows_saved;
                agg.rows_flagged += data.rows_flagged;
                agg.rows_already_saved += data.rows_already_saved;
                agg.rows_found += data.rows_found;
                for (const d of data.by_date) {
                    const t = (agg.by_date[d.date] ||= { date: d.date, found: 0, saved: 0, already_saved: 0, flagged: 0 });
                    t.found += d.found; t.saved += d.saved; t.already_saved += d.already_saved; t.flagged += d.flagged;
                }
                (data.other_dates || []).forEach(d => agg.other_dates.add(d));
                agg.warnings.push(...(data.warnings || []));
            } catch (err) {
                agg.errors.push(`${f.name}: ${err.message}`);
            }
        }
        stageTimers.current.forEach(clearTimeout);
        setUploading(null);
        setUploadResult({ ...agg, by_date: Object.values(agg.by_date).sort((a, b) => b.date.localeCompare(a.date)), other_dates: [...agg.other_dates] });

        // Jump the view to cover every date the upload touched, so the new
        // rows are visible straight away.
        const dates = Object.keys(agg.by_date).sort();
        if (dates.length) {
            const from = dates[0] < fromDate ? dates[0] : fromDate;
            const to = dates[dates.length - 1] > toDate ? dates[dates.length - 1] : toDate;
            if (from !== fromDate || to !== toDate) { setFromDate(from); setToDate(to); setQuickRange(null); setSelectedMonthYear(''); }
            else loadRows();
        }
    }

    // ── Inline edit — saving a yellow cell (even unchanged) confirms it ───
    function startEdit(row, col) {
        if (!canEdit || savingCell) return;
        const raw = row[col.key];
        const value = col.key === 'dispatch_date' ? String(raw).slice(0, 10) : (raw ?? '');
        setEditing({ id: row.id, key: col.key, value: String(value), original: String(value), flagged: !!row.flags?.[col.key] });
    }
    // Enter saves and then the input's blur fires too — the ref stops that
    // second call from saving twice.
    const savingRef = useRef(false);
    async function commitEdit() {
        if (!editing || savingRef.current) return;
        const { id, key, value, original, flagged } = editing;
        if (value === original && !flagged) { setEditing(null); return; }
        await saveCell(id, key, value);
    }
    async function saveCell(id, field, value) {
        if (savingRef.current) return;
        savingRef.current = true;
        setSavingCell(true);
        try {
            const res = await fetch(`${API_BASE}/po-register?action=update_cell`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id, field, value, username }),
            });
            const data = await res.json();
            if (!res.ok || !data.success) throw new Error(data.error || 'Save failed');
            setRows(prev => prev.map(r => (r.id === id ? data.row : r)));
            setEditing(null);
            showToast(field === '_row' ? '✓ Row confirmed' : '✓ Saved — recheck cleared');
        } catch (err) {
            showToast(`✕ ${err.message}`);
        }
        savingRef.current = false;
        setSavingCell(false);
    }
    async function deleteRow(row) {
        if (!window.confirm(`Delete this row?\n\n${registerDate(row.dispatch_date)} · ${row.party_name} · ${row.invoice_number || '-'}\n\nUse this only for a row that isn't in the register.`)) return;
        try {
            const res = await fetch(`${API_BASE}/po-register?action=delete_row`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id: row.id, username }),
            });
            const data = await res.json();
            if (!res.ok || !data.success) throw new Error(data.error || 'Delete failed');
            setRows(prev => prev.filter(r => r.id !== row.id));
            showToast('✓ Row deleted');
        } catch (err) {
            showToast(`✕ ${err.message}`);
        }
    }

    async function downloadExcel() {
        setDownloading(true);
        try {
            const qs = new URLSearchParams({ action: 'export', username });
            if (fromDate) qs.set('date_from', fromDate);
            if (toDate) qs.set('date_to', toDate);
            const res = await fetch(`${API_BASE}/po-register?${qs.toString()}`);
            if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.error || 'Download failed'); }
            const blob = await res.blob();
            const name = (res.headers.get('Content-Disposition') || '').match(/filename="([^"]+)"/)?.[1] || `PO_Register_${fromDate}_to_${toDate}.xlsx`;
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = name;
            a.click();
            URL.revokeObjectURL(url);
        } catch (err) {
            showToast(`✕ ${err.message}`);
        }
        setDownloading(false);
    }

    async function openHistory() {
        setHistoryOpen(true);
        try {
            const res = await fetch(`${API_BASE}/po-register?action=uploads`);
            const data = await res.json();
            setUploads(data.uploads || []);
        } catch { setUploads([]); }
    }

    return (
        <div style={S.page}>
            <style>{EMBEDDED_CSS}</style>
            {toast && <div style={S.toast}>{toast}</div>}

            {/* ── Hero: dark header + greyed-out "handwritten → Excel" art ── */}
            <div style={S.hero}>
                <div className="pr-hero-bg">
                    <NotebookPen className="pr-float pr-float-1" size={40} />
                    <FileSpreadsheet className="pr-float pr-float-2" size={36} />
                    <ScrollText className="pr-float pr-float-3" size={34} />
                    <Camera className="pr-float pr-float-4" size={30} />
                    <PenLine className="pr-float pr-float-5" size={30} />
                    <Table2 className="pr-float pr-float-6" size={34} />
                    <RegisterArt />
                </div>
                <div style={S.heroContent}>
                    <div>
                        <div style={S.kicker}>PO &amp; SO Lifecycle</div>
                        <h1 style={S.title}>PO Register <b>Data</b></h1>
                        <p style={S.subtitle}>Upload a photo of the handwritten dispatch register. It's read twice by AI, cross-checked and saved automatically. Anything doubtful is marked <span style={S.yellowChip}>yellow</span> for you to recheck.</p>
                    </div>
                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                        <button className="pr-btn" style={S.ghostBtn} onClick={openHistory}><History size={15} style={S.btnIcon} />Upload History</button>
                        <button className="pr-btn" style={S.downloadBtn} onClick={downloadExcel} disabled={downloading || rows.length === 0}>
                            {downloading ? 'Preparing…' : '⬇ Download Excel'}
                        </button>
                    </div>
                </div>

                {/* Drop zone */}
                <div
                    className={`pr-drop${dragOver ? ' pr-drop-over' : ''}${uploading ? ' pr-drop-busy' : ''}`}
                    style={S.drop}
                    onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={e => { e.preventDefault(); setDragOver(false); handleFiles(e.dataTransfer.files); }}
                    onClick={() => !uploading && fileInputRef.current?.click()}
                >
                    <input ref={fileInputRef} type="file" multiple accept={ACCEPT} style={{ display: 'none' }}
                        onChange={e => { handleFiles(e.target.files); e.target.value = ''; }} />
                    {uploading ? (
                        <div style={S.dropInner}>
                            <div className="pr-spinner" />
                            <div>
                                <div style={S.dropTitle}>{uploading.stage}</div>
                                <div style={S.dropHint}>{uploading.total > 1 ? `File ${uploading.index} of ${uploading.total} · ` : ''}{uploading.name || ''} — about a minute per photo, please keep this tab open</div>
                            </div>
                        </div>
                    ) : (
                        <div style={S.dropInner}>
                            <UploadCloud size={30} color="#C9BFE0" />
                            <div>
                                <div style={S.dropTitle}>{canEdit ? 'Drop register photos here, or click to choose' : 'View-only access — uploads are disabled'}</div>
                                <div style={S.dropHint}>WhatsApp / camera photos (JPG, PNG), PDF, ZIP or Excel · several files at once is fine · D-1 = {registerDate(isoOffset(-1))}</div>
                            </div>
                        </div>
                    )}
                </div>

                <div style={S.heroControlsRow}>
                    {[['d1', 'D-1 + Today'], ['yesterday', 'Yesterday'], ['today', 'Today'], ['last7', 'Last 7 days']].map(([k, label]) => (
                        <button key={k} className="pr-btn" style={{ ...S.heroQuickBtn, ...(quickRange === k ? S.heroQuickBtnActive : {}), flexShrink: 0 }} onClick={() => applyQuickRange(k)}>{label}</button>
                    ))}
                    <input className="pr-hero-input" style={{ ...S.heroInput, colorScheme: 'dark', flexShrink: 0 }} type="date" value={fromDate} onChange={e => onManualDateChange(setFromDate, e.target.value)} />
                    <span style={{ color: '#9A90AE', fontSize: 12.5, flexShrink: 0 }}>to</span>
                    <input className="pr-hero-input" style={{ ...S.heroInput, colorScheme: 'dark', flexShrink: 0 }} type="date" value={toDate} onChange={e => onManualDateChange(setToDate, e.target.value)} />
                    <select className="pr-hero-input" style={{ ...S.heroSelect, flexShrink: 0 }} value={selectedMonthYear} onChange={e => applyMonthYear(e.target.value)}>
                        <option value="">Select Month</option>
                        {MONTH_YEAR_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                    <button className="pr-btn" style={{ ...S.heroQuickBtn, ...(recheckOnly ? S.recheckBtnActive : {}), flexShrink: 0 }} onClick={() => setRecheckOnly(v => !v)}>
                        ⚠ Needs recheck only
                    </button>
                    <input className="pr-hero-input" style={{ ...S.heroInput, flex: 1, minWidth: 110 }} placeholder="🔍 Party, PO, invoice…" value={search} onChange={e => setSearch(e.target.value)} />
                </div>
                <p style={S.heroShowingText}>Showing <b style={{ color: '#fff' }}>{filteredRows.length}</b> of {rows.length} rows · {rangeLabel}</p>
            </div>

            {/* ── Upload result banner ───────────────────────────────────── */}
            {uploadResult && <UploadResult result={uploadResult} onClose={() => setUploadResult(null)} />}

            {/* ── KPIs ───────────────────────────────────────────────────── */}
            <div style={S.kpiRow}>
                <KpiTile label="Rows" value={summary.rows.toLocaleString('en-IN')} />
                <KpiTile label="PO Qty" value={inr(summary.qty)} />
                <KpiTile label="PO Amount" value={`₹${inr(summary.amount)}`} accent />
                <KpiTile label="Boxes" value={inr(summary.boxes)} />
                <KpiTile label="Weight (kg)" value={inr(summary.weight)} />
                <KpiTile label="Needs Recheck" value={summary.recheck} warn={summary.recheck > 0} onClick={summary.recheck ? () => setRecheckOnly(true) : undefined} />
            </div>

            {/* ── Table ──────────────────────────────────────────────────── */}
            {loading ? (
                <p style={S.emptyState}>Loading…</p>
            ) : error ? (
                <p style={{ ...S.emptyState, color: 'var(--pr-error)' }}>Failed to load: {error}</p>
            ) : filteredRows.length === 0 ? (
                <div style={{ ...S.card, ...S.emptyCard }}>
                    <NotebookPen size={34} color="#C9C2D6" />
                    <p style={{ margin: '10px 0 0' }}>{rows.length ? 'No rows match the filters.' : `No register entries for ${rangeLabel}. Upload a photo of the register to add them.`}</p>
                </div>
            ) : (
                <div style={S.card}>
                    {canEdit && <div style={S.tableHint}>Click any cell to correct it. Saving a <span style={S.yellowChip}>yellow</span> cell, even unchanged, marks it as checked.</div>}
                    <div style={S.scrollWrap}>
                        <div style={{ minWidth: 1180 }}>
                            <div style={{ ...S.gridRow, ...S.gridHeader, gridTemplateColumns: GRID_COLS }}>
                                <div style={S.headCell}>#</div>
                                {COLUMNS.map(c => <div key={c.key} style={{ ...S.headCell, textAlign: c.num ? 'right' : 'left' }}>{c.label}</div>)}
                                <div style={S.headCell} />
                            </div>
                            {groups.map(g => (
                                <div key={g.date}>
                                    <div style={S.groupRow}>
                                        <span style={{ fontWeight: 700 }}>{registerDate(g.date)}</span>
                                        <span style={S.groupMeta}>{g.list.length} rows · Qty {inr(g.qty)} · ₹{inr(g.amount)}</span>
                                    </div>
                                    {g.list.map((r, idx) => {
                                        const rowFlag = r.flags?._row;
                                        return (
                                            <div key={r.id} className="pr-row" style={{ ...S.gridRow, gridTemplateColumns: GRID_COLS, ...(rowFlag ? S.rowFlagged : {}) }}>
                                                <div style={{ ...S.cell, color: 'var(--pr-text-faint)' }} title={rowFlag || ''}>
                                                    {rowFlag ? <AlertTriangle size={14} color="#B7791F" /> : idx + 1}
                                                </div>
                                                {COLUMNS.map(c => {
                                                    const reason = r.flags?.[c.key];
                                                    const isEditing = editing && editing.id === r.id && editing.key === c.key;
                                                    return (
                                                        <div key={c.key}
                                                            className={`pr-cell${canEdit ? ' pr-cell-edit' : ''}${reason ? ' pr-cell-flag' : ''}`}
                                                            style={{ ...S.cell, ...(c.num ? S.numCell : {}), ...(c.bold ? { fontWeight: 600 } : {}), ...(c.mono ? S.mono : {}) }}
                                                            title={reason ? `Recheck: ${reason}` : (canEdit ? 'Click to edit' : '')}
                                                            onClick={() => !isEditing && startEdit(r, c)}>
                                                            {isEditing ? (
                                                                <input autoFocus className="pr-input" disabled={savingCell}
                                                                    type={c.editAs === 'date' ? 'date' : 'text'}
                                                                    value={editing.value}
                                                                    onChange={e => setEditing(ed => ({ ...ed, value: e.target.value }))}
                                                                    onKeyDown={e => { if (e.key === 'Enter') commitEdit(); if (e.key === 'Escape') setEditing(null); }}
                                                                    onBlur={commitEdit} />
                                                            ) : (c.render ? c.render(r) : (r[c.key] || '—'))}
                                                        </div>
                                                    );
                                                })}
                                                <div style={{ ...S.cell, display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                                                    {canEdit && rowFlag && (
                                                        <button className="pr-icon-btn" title="I checked — this row is in the register" onClick={() => saveCell(r.id, '_row', null)}><CheckCircle2 size={15} color="#5C7E60" /></button>
                                                    )}
                                                    {canEdit && (
                                                        <button className="pr-icon-btn" title="Delete row (not in the register)" onClick={() => deleteRow(r)}><Trash2 size={14} color="#C25E77" /></button>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* ── Upload history ─────────────────────────────────────────── */}
            {historyOpen && (
                <div style={S.modalOverlay} onClick={() => setHistoryOpen(false)}>
                    <div style={S.modal} onClick={e => e.stopPropagation()}>
                        <div style={S.modalHeader}>
                            <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>Upload History</h3>
                            <button style={S.closeBtn} onClick={() => setHistoryOpen(false)}>✕</button>
                        </div>
                        <div style={S.modalBody}>
                            {uploads.length === 0 ? <p style={{ ...S.emptyState, padding: 24 }}>No uploads yet.</p> : (
                                <table style={S.historyTable}>
                                    <thead>
                                        <tr>{['When', 'By', 'File', 'Dates', 'Saved', 'Recheck', 'Already saved', 'Status'].map(h => <th key={h} style={S.historyHead}>{h}</th>)}</tr>
                                    </thead>
                                    <tbody>
                                        {uploads.map(u => (
                                            <tr key={u.id}>
                                                <td style={S.historyCell}>{String(u.uploaded_at).slice(0, 16).replace('T', ' ')}</td>
                                                <td style={S.historyCell}>{u.uploaded_by}</td>
                                                <td style={{ ...S.historyCell, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={u.file_names}>{safeList(u.file_names)}</td>
                                                <td style={S.historyCell}>{(u.dates_found || '').split(',').filter(Boolean).map(registerDate).join(', ') || '—'}</td>
                                                <td style={S.historyCell}>{u.rows_inserted ?? '—'}</td>
                                                <td style={S.historyCell}>{u.rows_flagged ?? '—'}</td>
                                                <td style={S.historyCell}>{u.rows_duplicate ?? '—'}</td>
                                                <td style={{ ...S.historyCell, color: u.status === 'failed' ? 'var(--pr-error)' : u.status === 'done' ? 'var(--pr-green)' : 'var(--pr-text-muted)', fontWeight: 600 }} title={u.error || ''}>{u.status}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function safeList(json) {
    try { return JSON.parse(json).join(', '); } catch { return json || '—'; }
}

function UploadResult({ result, onClose }) {
    if (result.error) {
        return <div style={{ ...S.resultCard, borderColor: 'var(--pr-error)' }}><b style={{ color: 'var(--pr-error)' }}>{result.error}</b><button style={S.resultClose} onClick={onClose}>✕</button></div>;
    }
    const ok = !result.errors.length;
    return (
        <div style={{ ...S.resultCard, borderColor: ok ? '#BFD8C2' : '#F0C2CE' }}>
            <button style={S.resultClose} onClick={onClose}>✕</button>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                {ok ? <CheckCircle2 size={20} color="#5C7E60" /> : <AlertTriangle size={20} color="#C25E77" />}
                <span style={{ fontSize: 15, fontWeight: 700 }}>
                    {result.rows_saved} row{result.rows_saved === 1 ? '' : 's'} saved
                </span>
                {result.rows_flagged > 0 && <span style={{ ...S.pill, background: '#FFF3B0', color: '#7A5B00' }}>⚠ {result.rows_flagged} need recheck</span>}
                {result.rows_already_saved > 0 && <span style={{ ...S.pill, background: 'var(--pr-purple-tint)', color: 'var(--pr-purple)' }}>{result.rows_already_saved} already saved earlier</span>}
            </div>
            {result.by_date.length > 0 && (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
                    {result.by_date.map(d => (
                        <span key={d.date} style={S.dateChip}>
                            <b>{registerDate(d.date)}</b> · {d.saved} new{d.already_saved ? ` · ${d.already_saved} existing` : ''}{d.flagged ? ` · ${d.flagged} ⚠` : ''}
                        </span>
                    ))}
                </div>
            )}
            {[...result.errors.map(e => ({ e, err: true })), ...result.warnings.map(e => ({ e }))].map(({ e, err }, i) => (
                <div key={i} style={{ fontSize: 12.5, marginTop: 6, color: err ? 'var(--pr-error)' : 'var(--pr-text-muted)' }}>{err ? '✕ ' : '• '}{e}</div>
            ))}
        </div>
    );
}

function KpiTile({ label, value, accent, warn, onClick }) {
    return (
        <div style={{ ...S.kpiTile, ...(warn ? S.kpiWarn : {}), cursor: onClick ? 'pointer' : 'default' }} onClick={onClick}>
            <div style={{ fontSize: 22, fontWeight: 700, color: warn ? '#7A5B00' : accent ? 'var(--pr-purple)' : 'var(--pr-text)' }}>{value}</div>
            <div style={S.kpiLabel}>{label}</div>
        </div>
    );
}

// Greyed-out illustration in the hero: a handwritten register page turning
// into a spreadsheet grid. Pure decoration (pointer-events: none).
function RegisterArt() {
    return (
        <svg className="pr-art" width="330" height="150" viewBox="0 0 330 150" fill="none" stroke="#fff" strokeWidth="1.6" strokeLinecap="round">
            <g transform="rotate(-6 70 75)">
                <rect x="10" y="12" width="120" height="126" rx="6" />
                {[34, 50, 66, 82, 98, 114].map(y => <line key={y} x1="20" y1={y} x2="122" y2={y} strokeOpacity="0.5" />)}
                <line x1="38" y1="18" x2="38" y2="132" strokeOpacity="0.5" />
                <path d="M44 30 q6 -6 10 0 t10 0 t12 -2 M44 46 q5 -5 9 0 t11 0 M44 62 q6 -6 10 0 t10 0 t12 0 t8 -2 M44 78 q5 -5 9 0 t11 0 t9 0 M44 94 q6 -6 10 0 t12 0 M44 110 q5 -5 9 0 t11 0 t10 -1" />
                <path d="M22 30 l8 0 M22 46 l8 0 M22 62 l8 0 M22 78 l8 0" strokeOpacity="0.8" />
            </g>
            <path d="M150 75 h34 m-8 -7 l8 7 l-8 7" strokeWidth="2" />
            <rect x="200" y="22" width="120" height="106" rx="6" />
            {[44, 66, 88, 110].map(y => <line key={y} x1="200" y1={y} x2="320" y2={y} />)}
            {[230, 262, 292].map(x => <line key={x} x1={x} y1="22" x2={x} y2="128" />)}
            <rect x="200" y="22" width="120" height="22" fill="#fff" fillOpacity="0.25" stroke="none" />
            <rect x="262" y="66" width="30" height="22" fill="#FFE066" fillOpacity="0.35" stroke="none" />
        </svg>
    );
}

/* ---------------------------------------------------------------------- */
/* Styles — same token values as GiftingDispatch.jsx (own "pr-" prefix so  */
/* the two pages' :root variables never collide).                          */
/* ---------------------------------------------------------------------- */
const EMBEDDED_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;600;700&display=swap');
:root {
  --pr-surface: #fff; --pr-surface-subtle: #FBFAFD;
  --pr-border: #ECE7F2; --pr-border-soft: #E4DEEE;
  --pr-text: #231C30; --pr-text-muted: #8C82A0; --pr-text-faint: #9A90AE;
  --pr-accent-surface: #231C30;
  --pr-purple: #6E58A6; --pr-purple-tint: #EEE9F8;
  --pr-green: #5C7E60; --pr-green-tint: #E7F0E8;
  --pr-error: #C25E77; --pr-error-tint: #F7E5EA;
  --pr-flag: #FFF3B0; --pr-flag-strong: #FFE066;
  --pr-shadow-sm: 0 1px 2px rgba(35,28,48,0.05);
  --pr-shadow-md: 0 6px 20px rgba(35,28,48,0.10);
}
.pr-btn { font-family: 'Space Grotesk', sans-serif; transition: transform 0.15s ease, background 0.2s ease; cursor: pointer; display: inline-flex; align-items: center; }
.pr-btn:active:not(:disabled) { transform: scale(0.96); }
.pr-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.pr-row:nth-child(even) { background: var(--pr-surface-subtle); }
.pr-row:hover { background: var(--pr-purple-tint); }
.pr-cell-edit { cursor: text; }
.pr-cell-edit:hover { box-shadow: inset 0 0 0 1px var(--pr-border-soft); border-radius: 6px; }
.pr-cell-flag { background: var(--pr-flag) !important; box-shadow: inset 0 0 0 1px #F2D35B; border-radius: 6px; }
.pr-cell-flag:hover { background: var(--pr-flag-strong) !important; }
.pr-input { font-family: 'Space Grotesk', sans-serif; padding: 5px 8px; border-radius: 6px; border: 1.5px solid var(--pr-purple); background: #fff; color: var(--pr-text); font-size: 13px; width: 100%; box-sizing: border-box; outline: none; box-shadow: 0 0 0 3px rgba(110,88,166,0.18); }
.pr-icon-btn { background: transparent; border: none; cursor: pointer; padding: 4px; border-radius: 6px; display: inline-flex; }
.pr-icon-btn:hover { background: rgba(35,28,48,0.07); }
.pr-hero-input:focus { border-color: #A48FD1 !important; box-shadow: 0 0 0 3px rgba(164,143,209,0.25) !important; outline: none; background: #2A2140 !important; color: #fff !important; }
select.pr-hero-input { color-scheme: dark; }
.pr-hero-input option { background: #2A2140; color: #fff; }
.pr-hero-input::placeholder { color: #9A90AE; }
.pr-drop { transition: border-color 0.2s ease, background 0.2s ease; cursor: pointer; }
.pr-drop:hover, .pr-drop-over { border-color: #A48FD1 !important; background: rgba(164,143,209,0.12) !important; }
.pr-drop-busy { cursor: progress; }
.pr-spinner { width: 26px; height: 26px; border-radius: 50%; border: 3px solid rgba(255,255,255,0.18); border-top-color: #A48FD1; animation: pr-spin 0.9s linear infinite; flex-shrink: 0; }
@keyframes pr-spin { to { transform: rotate(360deg); } }
* { scrollbar-width: thin; scrollbar-color: rgba(110,88,166,0.35) transparent; }

.pr-hero-bg { position: absolute; inset: 0; overflow: hidden; border-radius: 20px; pointer-events: none; color: #fff; }
.pr-float { position: absolute; opacity: 0.13; animation: pr-drift 9s ease-in-out infinite; }
.pr-float-1 { top: 12%; left: 5%;  animation-duration: 8s; }
.pr-float-2 { top: 64%; left: 12%; animation-duration: 10s; animation-delay: 1s; }
.pr-float-3 { top: 16%; left: 44%; animation-duration: 7.5s; animation-delay: 0.5s; }
.pr-float-4 { top: 74%; left: 40%; animation-duration: 9.5s; animation-delay: 2s; }
.pr-float-5 { top: 40%; left: 28%; animation-duration: 11s; animation-delay: 1.5s; }
.pr-float-6 { top: 8%;  left: 92%; animation-duration: 8.5s; animation-delay: 0.8s; }
.pr-art { position: absolute; right: 230px; top: 18px; opacity: 0.11; }
@media (max-width: 1100px) { .pr-art { display: none; } }
@keyframes pr-drift {
  0%, 100% { transform: translateY(0) rotate(0deg); }
  50% { transform: translateY(-14px) rotate(7deg); }
}
`;

const S = {
    page: { padding: 28, fontFamily: "'Space Grotesk', sans-serif", color: 'var(--pr-text)' },
    toast: { position: 'fixed', top: 18, left: '50%', transform: 'translateX(-50%)', background: '#231C30', color: '#fff', padding: '10px 20px', borderRadius: 20, fontSize: 13, zIndex: 9999, boxShadow: '0 4px 12px rgba(0,0,0,0.25)' },
    hero: { position: 'relative', background: 'linear-gradient(135deg, #2A2140 0%, #1A1529 55%, #120D1D 100%)', border: '1px solid #2A2338', borderRadius: 20, padding: '32px 32px', marginBottom: 18, overflow: 'hidden', boxShadow: '0 10px 30px rgba(20,16,31,0.3)' },
    heroContent: { position: 'relative', zIndex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 20, flexWrap: 'wrap' },
    kicker: { fontSize: 12, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#A48FD1' },
    title: { fontSize: 30, fontWeight: 500, margin: '6px 0 10px', color: '#fff', letterSpacing: '-0.01em' },
    subtitle: { fontSize: 14.5, color: '#B2AAC4', margin: 0, maxWidth: 520, lineHeight: 1.55 },
    yellowChip: { background: '#FFF3B0', color: '#7A5B00', padding: '1px 7px', borderRadius: 6, fontWeight: 600 },
    downloadBtn: { padding: '12px 20px', borderRadius: 10, border: 'none', background: 'var(--pr-green)', color: '#fff', fontWeight: 700, fontSize: 13.5 },
    ghostBtn: { padding: '12px 18px', borderRadius: 10, border: '1.5px solid rgba(255,255,255,0.25)', background: 'rgba(255,255,255,0.08)', color: '#fff', fontWeight: 600, fontSize: 13.5 },
    btnIcon: { marginRight: 7 },

    drop: { position: 'relative', zIndex: 1, marginTop: 22, border: '1.5px dashed rgba(255,255,255,0.28)', borderRadius: 14, background: 'rgba(255,255,255,0.05)', padding: '18px 20px' },
    dropInner: { display: 'flex', alignItems: 'center', gap: 16 },
    dropTitle: { color: '#fff', fontWeight: 600, fontSize: 14.5 },
    dropHint: { color: '#9A90AE', fontSize: 12.5, marginTop: 3 },

    heroControlsRow: { position: 'relative', zIndex: 1, display: 'flex', flexWrap: 'nowrap', gap: 8, alignItems: 'center', marginTop: 18, paddingTop: 16, borderTop: '1px solid rgba(255,255,255,0.12)', overflowX: 'auto' },
    heroQuickBtn: { padding: '8px 14px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.18)', background: 'rgba(255,255,255,0.06)', color: '#C9BFE0', fontWeight: 500, fontSize: 12.5, whiteSpace: 'nowrap' },
    heroQuickBtnActive: { background: '#A48FD1', borderColor: '#A48FD1', color: '#1A1529', fontWeight: 700 },
    recheckBtnActive: { background: '#FFE066', borderColor: '#FFE066', color: '#3D2E00', fontWeight: 700 },
    heroInput: { padding: '8px 12px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.18)', background: 'rgba(255,255,255,0.08)', color: '#fff', fontSize: 12.5, fontFamily: "'Space Grotesk', sans-serif" },
    heroSelect: { padding: '8px 12px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.18)', background: '#2A2140', color: '#fff', fontSize: 12.5, fontFamily: "'Space Grotesk', sans-serif" },
    heroShowingText: { position: 'relative', zIndex: 1, fontSize: 12.5, color: '#9A90AE', margin: '12px 0 0' },

    resultCard: { position: 'relative', background: 'var(--pr-surface)', border: '1.5px solid', borderRadius: 14, padding: '16px 44px 16px 18px', marginBottom: 18, boxShadow: 'var(--pr-shadow-sm)' },
    resultClose: { position: 'absolute', top: 12, right: 12, background: 'var(--pr-surface-subtle)', border: 'none', borderRadius: 8, width: 26, height: 26, cursor: 'pointer', color: 'var(--pr-text-muted)' },
    pill: { fontSize: 12, fontWeight: 700, padding: '3px 10px', borderRadius: 999 },
    dateChip: { fontSize: 12, padding: '4px 10px', borderRadius: 8, background: 'var(--pr-surface-subtle)', border: '1px solid var(--pr-border)' },

    kpiRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12, marginBottom: 18 },
    kpiTile: { background: 'var(--pr-surface)', border: '1px solid var(--pr-border)', borderRadius: 14, padding: '14px 16px', boxShadow: 'var(--pr-shadow-sm)' },
    kpiWarn: { background: '#FFFBEA', borderColor: '#F2D35B' },
    kpiLabel: { fontSize: 11, color: 'var(--pr-text-faint)', textTransform: 'uppercase', letterSpacing: '0.04em', marginTop: 5, fontWeight: 600 },

    emptyState: { padding: 48, textAlign: 'center', color: 'var(--pr-text-faint)', fontSize: 14 },
    emptyCard: { padding: 48, textAlign: 'center', color: 'var(--pr-text-faint)', fontSize: 14, display: 'flex', flexDirection: 'column', alignItems: 'center' },
    card: { background: 'var(--pr-surface)', border: '1px solid var(--pr-border)', borderRadius: 16, overflow: 'hidden', boxShadow: 'var(--pr-shadow-md)' },
    tableHint: { fontSize: 12, color: 'var(--pr-text-muted)', padding: '10px 14px', borderBottom: '1px solid var(--pr-border)', background: 'var(--pr-surface-subtle)' },
    scrollWrap: { overflow: 'auto', maxHeight: '68vh' },
    gridRow: { display: 'grid', alignItems: 'center', borderBottom: '1px solid var(--pr-border)' },
    gridHeader: { position: 'sticky', top: 0, background: 'var(--pr-accent-surface)', color: '#fff', fontSize: 10.5, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', zIndex: 2 },
    headCell: { padding: '13px 10px', overflow: 'hidden', whiteSpace: 'nowrap' },
    groupRow: { display: 'flex', alignItems: 'center', gap: 12, padding: '9px 12px', background: 'var(--pr-purple-tint)', borderBottom: '1px solid var(--pr-border)', fontSize: 13, color: 'var(--pr-purple)', position: 'sticky', top: 38, zIndex: 1 },
    groupMeta: { fontSize: 12, color: 'var(--pr-text-muted)' },
    rowFlagged: { boxShadow: 'inset 4px 0 0 #F2C94C', background: '#FFFCEB' },
    cell: { padding: '10px 10px', fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', margin: '2px 0' },
    numCell: { textAlign: 'right', fontVariantNumeric: 'tabular-nums' },
    mono: { fontVariantNumeric: 'tabular-nums', letterSpacing: '0.01em' },

    modalOverlay: { position: 'fixed', inset: 0, background: 'rgba(35,28,48,0.5)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 },
    modal: { background: 'var(--pr-surface)', borderRadius: 18, width: 900, maxWidth: '100%', maxHeight: '86vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.35)' },
    modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px 22px', borderBottom: '1px solid var(--pr-border)' },
    closeBtn: { background: 'var(--pr-surface-subtle)', border: 'none', borderRadius: 8, width: 30, height: 30, fontSize: 14, cursor: 'pointer', color: 'var(--pr-text-muted)' },
    modalBody: { padding: '14px 22px 20px', overflowY: 'auto', flex: 1 },
    historyTable: { width: '100%', borderCollapse: 'collapse', fontSize: 12.5 },
    historyHead: { padding: '8px 8px', textAlign: 'left', fontSize: 10.5, fontWeight: 700, color: 'var(--pr-text-faint)', textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: '2px solid var(--pr-border)' },
    historyCell: { padding: '8px 8px', borderBottom: '1px solid var(--pr-border)' },
};
